/**
 * <atb-recently-viewed>
 * --------------------------------------------------------------------------
 * Reads atb-recently-viewed-tracker.js's localStorage list, fetches fresh
 * product data via /products/<handle>.js for each (with sessionStorage
 * cache to avoid re-fetching within a session), and renders a carousel of
 * cards that link back to each product's PDP.
 *
 * Behavior:
 *  - Hidden by default in liquid (`hidden` attr on the host element). JS
 *    un-hides only after we confirm there's at least one product to show
 *    after filtering. Eliminates the first-visit empty-state flash.
 *  - Excludes the current PDP product so customers don't see themselves
 *    listed as "Recently viewed."
 *  - Lazy-hydrates via IntersectionObserver (400px rootMargin) — same
 *    pattern as atb-pdp-upsell and atb-bundle-widget.
 *  - If a previously-viewed product is now unavailable (404 from
 *    /products/<handle>.js), we just skip it and let the next handle
 *    take its place.
 */

class AtbRecentlyViewed extends HTMLElement {
  constructor() {
    super();
    this.grid = this.querySelector('.atb-recently-viewed__grid');
    this.template = this.querySelector('.atb-recently-viewed__card-template');

    this.currentProductId = String(this.dataset.currentProductId || '');
    this.currentHandle = String(this.dataset.currentProductHandle || '');
    this.limit = parseInt(this.dataset.limit, 10) || 4;

    const colsDesktop = parseInt(this.dataset.columnsDesktop, 10);
    const colsMobile = parseInt(this.dataset.columnsMobile, 10);
    if (Number.isFinite(colsDesktop)) this.style.setProperty('--atb-rv-columns-desktop', colsDesktop);
    if (Number.isFinite(colsMobile)) this.style.setProperty('--atb-rv-columns-mobile', colsMobile);

    this.routeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this.STORAGE_KEY = 'atb_recently_viewed';
    this.SESSION_CACHE_PREFIX = 'atb_rv_cache_';
    this._loaded = false;
  }

  connectedCallback() {
    if (!this.grid || !this.template) {
      this.remove();
      return;
    }

    // Read localStorage upfront so we can bail BEFORE skeleton is shown
    // if the customer has no history. Avoids a flash of empty skeleton
    // on a first-visit customer who is also exposed to the section.
    const handles = this._readHandles();
    if (handles.length === 0) {
      this.remove();
      return;
    }

    // Un-hide now that we know we have data to show. Skeleton will be
    // visible until the fetches resolve.
    this.removeAttribute('hidden');

    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this._observer.disconnect();
              this.load(handles);
            }
          });
        },
        { rootMargin: '400px 0px' }
      );
      this._observer.observe(this);
    } else {
      this.load(handles);
    }
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
  }

  /**
   * Read the recently-viewed list from localStorage, filter out the
   * current PDP product, and trim to limit. Returns an array of handles
   * in MRU order.
   */
  _readHandles() {
    let raw;
    try {
      raw = localStorage.getItem(this.STORAGE_KEY);
    } catch (_) {
      return [];
    }
    if (!raw) return [];
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (_) {
      return [];
    }
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((item) => item && typeof item.handle === 'string' && item.handle.length > 0)
      .map((item) => item.handle)
      .filter((handle) => handle !== this.currentHandle)
      .slice(0, this.limit);
  }

  async load(handles) {
    if (this._loaded) return;
    this._loaded = true;

    try {
      // Fetch all products in parallel. /products/<handle>.js returns
      // the same JSON shape as Shopify's recommendations endpoint.
      const products = await Promise.all(
        handles.map((handle) => this._fetchProduct(handle))
      );

      // Drop nulls (404s, network failures, archived products) but
      // preserve MRU order from localStorage.
      const valid = products.filter((p) => p && p.id);

      // Additional safety filter: exclude current product if it slipped
      // through (matches by id, in case the handle lookup miss-matched
      // a recently renamed product).
      const filtered = this.currentProductId
        ? valid.filter((p) => String(p.id) !== this.currentProductId)
        : valid;

      if (filtered.length === 0) {
        this.remove();
        return;
      }

      this.render(filtered);
    } catch (err) {
      console.warn('[atb-recently-viewed] load failed:', err);
      this.remove();
    }
  }

  /**
   * Fetch a product's full JSON via /products/<handle>.js.
   * Uses cache:'no-cache' so the browser revalidates with Shopify on every load:
   * a changed primary image (or price/title) shows up immediately instead of a
   * stale featured_image. The payload is tiny and unchanged responses come back
   * as cheap 304s, so we intentionally do NOT persist it in sessionStorage —
   * that no-expiry cache was the cause of stale images after a primary-image swap.
   * Returns null on 404 / network failure / bad JSON.
   */
  async _fetchProduct(handle) {
    try {
      const url = `${this.routeRoot}products/${encodeURIComponent(handle)}.js`;
      const res = await fetch(url, { credentials: 'same-origin', cache: 'no-cache' });
      if (!res.ok) return null;
      const product = await res.json();
      if (!product || !product.id) return null;
      return product;
    } catch (_) {
      return null;
    }
  }

  render(products) {
    this.grid.innerHTML = '';

    products.forEach((product) => {
      const card = this.template.content.firstElementChild.cloneNode(true);
      card.dataset.productId = product.id;

      const productUrl = product.url || `${this.routeRoot}products/${product.handle}`;
      const mediaLink = card.querySelector('.atb-recently-viewed__media');
      const img = card.querySelector('.atb-recently-viewed__image');
      const titleLink = card.querySelector('.atb-recently-viewed__title');
      const priceEl = card.querySelector('.atb-recently-viewed__price');

      mediaLink.href = productUrl;
      mediaLink.setAttribute('aria-hidden', 'false');
      mediaLink.removeAttribute('tabindex');
      mediaLink.setAttribute('aria-label', product.title);

      titleLink.href = productUrl;
      titleLink.textContent = product.title;

      const imgSrc = this._extractImageSrc(product);
      if (imgSrc) {
        img.src = this._sizedImage(imgSrc, '600x600');
        img.alt =
          product.featured_image && product.featured_image.alt
            ? product.featured_image.alt
            : product.title;
      } else {
        img.remove();
      }

      priceEl.textContent = this._formatMoney(product.price);

      this.grid.appendChild(card);
    });
  }

  // --- helpers ------------------------------------------------------------

  _extractImageSrc(product) {
    if (!product) return null;
    if (typeof product.featured_image === 'string') return product.featured_image;
    if (product.featured_image && (product.featured_image.url || product.featured_image.src)) {
      return product.featured_image.url || product.featured_image.src;
    }
    if (Array.isArray(product.images) && product.images.length) {
      const first = product.images[0];
      return typeof first === 'string' ? first : first.src || first.url;
    }
    return null;
  }

  _sizedImage(src, size) {
    if (!src) return '';
    let url = src.startsWith('//') ? `https:${src}` : src;
    url = url.replace(/(\.(?:jpe?g|png|gif|webp|avif))(\?|$)/i, `_${size}$1$2`);
    return url;
  }

  _formatMoney(price) {
    const cents = typeof price === 'string' ? parseInt(price, 10) : price;
    if (!Number.isFinite(cents)) return '';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const format = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
      try {
        return window.Shopify.formatMoney(cents, format);
      } catch (_) {
        /* fall through */
      }
    }

    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }
}

if (!customElements.get('atb-recently-viewed')) {
  customElements.define('atb-recently-viewed', AtbRecentlyViewed);
}
